import React from 'react';
import { connect } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import { createSelector } from 'reselect';

import {
  createNewUserAccount,
  fetchBrokerageOfferData,
  fetchOtherBids,
  handleReturningUser,
  maybeAcceptInvitation,
  storeInvitationTokenFromLocalStorage,
} from '../../store/brokerageOfferFlow/actions';
import * as brokerageOfferFlowSelectors from '../../store/brokerageOfferFlow/selectors';

import Account from '../../components/brokerageOfferFlow/account/Account';
import AllSet from '../../components/brokerageOfferFlow/AllSet';
import Error from '../../components/brokerageOfferFlow/Error';
import Welcome from '../../components/brokerageOfferFlow/Welcome';
import Layout from '../../components/common/Layout';
import { IBrokerageInfo } from '../../declarations';
import normalizeUrl from '../../helpers/normalizeUrl';
import OfferDetails from './OfferDetails';
import OfferLayout from './OfferLayout';
import PriceJustification from './PriceJustification';
import PurchaseAgreement from './PurchaseAgreement';

interface IMatch {
  path: string;
  params: { token: string };
  url: string;
}

interface IProps {
  leadFirstName: string;
  acqCxLeadFullName: string;
  acqCxLeadImageUrl: string;
  acqCxLeadPhoneNumber: string;
  createNewUserAccount: (email: string, password: string) => void;
  fetchOtherBids: (token: string) => void;
  fetchBrokerageOfferData: (token: string) => void;
  handleReturningUser: (email: string, password: string) => void;
  invitationData: Map<string, any>;
  isBrokerageOfferDataLoading: boolean;
  loginError: string;
  maybeAcceptInvitation: () => void;
  storeInvitationTokenFromLocalStorage: (brokerageOfferToken: string) => void;
  match: IMatch;
  brokerageInfo: IBrokerageInfo;
}

class BrokerageOfferFlow extends React.Component<IProps> {
  public componentDidMount() {
    const {
      fetchBrokerageOfferData,
      storeInvitationTokenFromLocalStorage,
      fetchOtherBids,
    } = this.props;
    const { token } = this.props.match.params;

    fetchBrokerageOfferData(token);
    fetchOtherBids(token);
    storeInvitationTokenFromLocalStorage(token);
  }

  public componentProps() {
    const { fetchBrokerageOfferData, isBrokerageOfferDataLoading, ...rest } = this.props;

    return { ...rest };
  }

  public welcomeComponent = () => {
    const nextUrl = `${normalizeUrl(this.props.match.url)}/price`;

    return (
      <OfferLayout
        disclaimerType="trusted-buyer"
        headerType="BROKERAGE"
        footerType="BROKERAGE"
        brokerageInfo={this.props.brokerageInfo}
        primaryBtnUrl={nextUrl}
        primaryBtnText={"Let's get started"}
      >
        <Welcome {...this.componentProps()} />
      </OfferLayout>
    );
  };

  public accountComponent = () => {
    const { invitationData, createNewUserAccount, handleReturningUser, loginError } = this.props;

    if (!invitationData) {
      // Probably waiting for the network call to complete. Render an empty layout for now.
      return <Layout />;
    }

    return (
      <Layout>
        <Account
          email={invitationData.get('email')}
          existingUser={invitationData.get('existing_user')}
          loginError={loginError}
          newUserTitle="Ready to move forward?"
          newUserSubtitle="Finish setting up your account to continue with your offer."
          onNewUserSubmit={createNewUserAccount}
          onReturningUserSubmit={handleReturningUser}
        />
      </Layout>
    );
  };

  public completeComponent = () => (
    <OfferLayout
      headerType="BROKERAGE"
      footerType="BROKERAGE"
      brokerageInfo={this.props.brokerageInfo}
      disclaimerType="trusted-buyer"
    >
      <AllSet {...this.componentProps()} />
    </OfferLayout>
  );

  public errorComponent = () => (
    <Layout>
      <Error />
    </Layout>
  );

  public render() {
    const { match, isBrokerageOfferDataLoading } = this.props;

    if (isBrokerageOfferDataLoading) {
      return <OfferLayout footerType="NONE" headerType="BROKERAGE" />;
    }

    return (
      <div>
        <Switch>
          <Route exact={true} path={match.path + '/'} render={this.welcomeComponent} />
          <Route exact={true} path={match.path + '/welcome'} render={this.welcomeComponent} />
          <Route exact={true} path={match.path + '/price'} component={PriceJustification} />
          <Route exact={true} path={match.path + '/details'} component={OfferDetails} />
          <Route exact={true} path={match.path + '/account'} component={this.accountComponent} />
          <Route
            exact={true}
            path={match.path + '/purchase-agreement'}
            component={PurchaseAgreement}
          />
          <Route exact={true} path={match.path + '/complete'} component={this.completeComponent} />
          <Route exact={true} path={match.path + '/error'} component={this.errorComponent} />
        </Switch>
      </div>
    );
  }
}

const mapStateToProps = createSelector(
  [
    brokerageOfferFlowSelectors.acqCxLeadFullName,
    brokerageOfferFlowSelectors.acqCxLeadImageUrl,
    brokerageOfferFlowSelectors.acqCxLeadPhoneNumber,
    brokerageOfferFlowSelectors.invitationData,
    brokerageOfferFlowSelectors.isBrokerageOfferDataLoading,
    brokerageOfferFlowSelectors.leadFirstName,
    brokerageOfferFlowSelectors.loginError,
    brokerageOfferFlowSelectors.brokerageInfo,
  ],
  (
    acqCxLeadFullName: string,
    acqCxLeadImageUrl: string,
    acqCxLeadPhoneNumber: string,
    invitationData: Map<string, any>,
    isBrokerageOfferDataLoading: boolean,
    leadFirstName: string,
    loginError: string,
    brokerageInfo: IBrokerageInfo,
  ) => ({
    acqCxLeadFullName,
    acqCxLeadImageUrl,
    acqCxLeadPhoneNumber,
    brokerageInfo,
    invitationData,
    isBrokerageOfferDataLoading,
    leadFirstName,
    loginError,
  }),
);

const mapDispatchToProps = {
  createNewUserAccount,
  fetchBrokerageOfferData,
  fetchOtherBids,
  handleReturningUser,
  maybeAcceptInvitation,
  storeInvitationTokenFromLocalStorage,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(BrokerageOfferFlow);
